import { Column } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'

import Text from '@/shared/components/common/text'
import { Button } from '@/shared/components/ui/button'

import Show from '../../../show'

interface IDataTableColumnHeaderProps<TData, TValue> {
  column: Column<TData, TValue>
  title: string
  subtitle?: string
  manualSorting?: boolean
}

export default function DataTableColumnHeader<TData, TValue>({
  title,
  subtitle,
  column,
  manualSorting = false,
}: IDataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return (
      <div className="flex flex-col gap-0.5">
        <Text className="text-sm font-medium text-client-muted">{title}</Text>
        {subtitle ? (
          <Text className="text-xs font-normal text-client-neutral-500">{subtitle}</Text>
        ) : null}
      </div>
    )
  }

  const handleToggleSort = () => {
    // Only toggle sorting internally if not using manual sorting
    // When manualSorting is true, the parent DataTable handles the click
    if (!manualSorting) {
      column.toggleSorting()
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-auto items-start p-0 hover:bg-transparent"
      onClick={handleToggleSort}
    >
      <div className="flex flex-col items-start gap-0.5">
        <div className="flex items-center self-center">
          <Text className="text-sm font-medium text-client-muted">{title}</Text>
          <Show
            when={column.getIsSorted()}
            fallback={<ArrowUpDown className="ml-1 size-4 text-client-muted" />}
          >
            {(sorted) => {
              const Icon = sorted === 'desc' ? ArrowDown : ArrowUp
              return <Icon className="ml-1 size-4 text-client-muted" />
            }}
          </Show>
        </div>
        {subtitle ? (
          <Text className="text-xs font-normal text-client-neutral-500">{subtitle}</Text>
        ) : null}
      </div>
    </Button>
  )
}
